import { useMemo } from 'react';
import {
  Checkbox,
  Input,
  Select,
  SelectItem,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@heroui/react';
import { Paintbrush } from 'lucide-react';
import { useFinishes } from '@/api/finishes';
import type { PanelFinish, Finish } from '@estimator/shared';

interface PanelFinishControlsProps {
  finish: PanelFinish;
  onChange: (updates: Partial<PanelFinish>) => void;
  /** Name of framing.default_finish_id, for the "Use template default" label. */
  defaultFinishName: string | null;
}

/**
 * Tick / sides / complexity / profile-override cluster for one framing panel.
 * Collapsed to a single checkbox until ticked, so rows that receive no finish
 * look exactly as they did before this feature existed.
 */
export default function PanelFinishControls({
  finish,
  onChange,
  defaultFinishName,
}: PanelFinishControlsProps) {
  const { data: finishes = [] } = useFinishes();
  const activeFinishes = useMemo(
    () => finishes.filter((f: Finish) => f.active),
    [finishes],
  );

  return (
    <div className="flex items-center gap-1.5 flex-shrink-0">
      <Checkbox
        size="sm"
        aria-label="Apply finish to this panel"
        isSelected={finish?.enabled ?? false}
        onValueChange={(enabled) => onChange({ enabled })}
      />

      {finish?.enabled && (
        <>
          <Select
            aria-label="Finished sides"
            className="w-16"
            size="sm"
            selectedKeys={[String(finish.sides ?? 1)]}
            onSelectionChange={(keys) => {
              const v = Number(Array.from(keys)[0]);
              onChange({ sides: v === 2 ? 2 : 1 });
            }}
            classNames={{ trigger: 'bg-[var(--bg-panel)] min-h-8 h-8' }}
          >
            <SelectItem key="1">1</SelectItem>
            <SelectItem key="2">2</SelectItem>
          </Select>

          <Input
            type="number"
            aria-label="Finish complexity multiplier"
            title="Complexity multiplier — scales this panel's finishing minutes, not its cost"
            value={(finish.complexity ?? 1).toString()}
            onValueChange={(v) => onChange({ complexity: Math.max(0, Number(v) || 0) })}
            size="sm"
            className="w-20"
            startContent={
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>×</span>
            }
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)] h-8 min-h-8' }}
          />

          <Popover placement="left">
            <PopoverTrigger>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                aria-label="Finish profile for this panel"
              >
                <Paintbrush
                  size={13}
                  style={{ color: finish.finish_id ? '#e67e22' : 'var(--text-muted)' }}
                />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="p-3 w-72"
              style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' }}
            >
              <Select
                label="Finish profile"
                size="sm"
                selectedKeys={[finish.finish_id ?? '__default__']}
                onSelectionChange={(keys) => {
                  const id = Array.from(keys)[0] as string;
                  onChange({ finish_id: id === '__default__' ? null : id });
                }}
                classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
              >
                <SelectItem key="__default__">
                  {`Use template default${defaultFinishName ? ` (${defaultFinishName})` : ''}`}
                </SelectItem>
                <>
                  {activeFinishes.map((f: Finish) => (
                    <SelectItem key={f.id}>
                      {`${f.name} — $${Number(f.cost_per_sqft ?? 0).toFixed(2)}/SF`}
                    </SelectItem>
                  ))}
                </>
              </Select>
            </PopoverContent>
          </Popover>
        </>
      )}
    </div>
  );
}
